// The Depth API (SERVER.md): every request carries the session token; the server's own errors are recognised so a
// 401 signed_out anywhere signs the app out.
import { API_BASE } from "../config";

export type Provider = "openrouter" | "anthropic" | "openai" | "gemini" | "typesafe";
export const PROVIDER_NAME: Record<Provider, string> = {
  openrouter: "OpenRouter", anthropic: "Anthropic", openai: "OpenAI", gemini: "Gemini", typesafe: "TypeSafe",
};

export type Problem =
  | { kind: "signedOut" } | { kind: "notAllowed"; identity: string } | { kind: "providerUnavailable" }
  | { kind: "fetchUnavailable" } | { kind: "syncUnavailable" } | { kind: "rateLimited" };

export function problemMessage(p: Problem): string {
  switch (p.kind) {
    case "signedOut": return "Sign in to use this.";
    case "notAllowed": return `This account isn't allowed yet. Ask the owner to add ${p.identity}.`;
    case "providerUnavailable": return "Not available on this server.";
    case "fetchUnavailable": return "Fetching isn't available for this account.";
    case "syncUnavailable": return "Sync isn't available on this server.";
    case "rateLimited": return "Too many requests. Try again in a moment.";
  }
}

/** The server's own error (`{"error": "…"}`), or null when the reply came from the provider. */
export function problem(status: number, body: unknown): Problem | null {
  if (status < 400 || status >= 600 || !body || typeof body !== "object") return null;
  const err = (body as any).error;
  if (typeof err !== "string") return null;
  if (status === 401 && err === "signed_out") return { kind: "signedOut" };
  if (status === 403 && err === "not_allowed") return { kind: "notAllowed", identity: (body as any).identity ?? "your account" };
  if (status === 503 && err === "provider_unavailable") return { kind: "providerUnavailable" };
  if (status === 403 && err === "fetch_unavailable") return { kind: "fetchUnavailable" };
  if (status === 503 && err === "sync_unavailable") return { kind: "syncUnavailable" };
  if (status === 429 && err === "rate_limited") return { kind: "rateLimited" };
  return null;
}

const SESSION_KEY = "depth.session:" + API_BASE;

export const session = {
  get token(): string | null { try { return localStorage.getItem(SESSION_KEY); } catch { return null; } },
  set(token: string | null) {
    try { if (token) localStorage.setItem(SESSION_KEY, token); else localStorage.removeItem(SESSION_KEY); } catch { /* private mode */ }
  },
};

/** Set by the account: called when any reply says signed_out. */
export let onSignedOut: () => void = () => {};
export function setSignedOutHandler(fn: () => void) { onSignedOut = fn; }

export const proxyBase = (p: Provider) => `${API_BASE}/v1/proxy/${p}`;

export interface ApiInit extends RequestInit { json?: unknown; auth?: boolean }

export async function api(path: string, init: ApiInit = {}): Promise<Response> {
  const headers = new Headers(init.headers);
  const token = session.token;
  if (init.auth !== false && token) headers.set("Authorization", "Bearer " + token);
  let body = init.body;
  if (init.json !== undefined) { headers.set("Content-Type", "application/json"); body = JSON.stringify(init.json); }
  const url = path.startsWith("http") ? path : API_BASE + path;
  const res = await fetch(url, { ...init, headers, body });
  if (res.status === 401) {
    const copy = res.clone();
    copy.json().then((b) => { if (problem(401, b)?.kind === "signedOut") onSignedOut(); }).catch(() => {});
  }
  return res;
}

/** Reads a non-OK reply's body (bounded) as text and JSON. */
export async function errorBody(res: Response): Promise<{ text: string; json: any }> {
  const text = (await res.text().catch(() => "")).slice(0, 4000);
  let json: any = null;
  try { json = JSON.parse(text); } catch { /* not JSON */ }
  return { text, json };
}
